import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AspectRatioControls from './AspectRatioControls';
import initializeScene, { disposeScene } from '../three/SceneHandler';
import SceneController from './SceneController';
import LabelsPanel from './LabelsPanel';
import { authFetch } from '../utils/apiHelper';
import { API_URL } from '../config';
import { getViewingModelId } from '../utils/shareSession';

const Scene = () => {
  const [isBottomHtmlLoaded, setIsBottomHtmlLoaded] = useState(false);
  const [loadCrashed, setLoadCrashed] = useState(null);
  const navigate = useNavigate();
  const modelNameRef = useRef(null);

  // The centered #model-name span (viewer-top.html) is position:absolute and
  // white-space:nowrap by default, so a long title slides *under* the header's
  // left logo and right icon cluster instead of stopping at them. Measure the
  // gap actually available between those two clusters and, only once the title
  // no longer fits on one line there, wrap it to (up to) two lines — centered
  // if the centered gap is still usable, otherwise left-aligned against the
  // brand so narrow/mobile widths get the full width between logo and icons
  // rather than the much smaller symmetric centered box.
  const fitModelName = () => {
    const el = document.getElementById('model-name');
    const nav = document.getElementById('top-nav');
    if (!el || !nav || !el.textContent) return;
    const leftEl = nav.querySelector('.top-wrapper');
    const rightEl = nav.querySelector('.capture-viewer-top');
    if (!leftEl || !rightEl) return;

    const opRect = (el.offsetParent || nav).getBoundingClientRect();
    const navR = nav.getBoundingClientRect();
    const leftR = leftEl.getBoundingClientRect();
    const rightR = rightEl.getBoundingClientRect();
    const GAP = 12;
    const centerX = navR.left + navR.width / 2;
    const availCentered = 2 * Math.max(0, Math.min(
      centerX - (leftR.right + GAP),
      (rightR.left - GAP) - centerX,
    ));
    const availLeft = Math.max(0, (rightR.left - GAP) - (leftR.right + GAP));

    // Reset to the one-line centered state and measure its natural width.
    el.style.top = '50%';
    el.style.whiteSpace = 'nowrap';
    el.style.maxWidth = 'none';
    el.style.display = 'block';
    el.style.webkitLineClamp = '';
    el.style.removeProperty('line-clamp');
    el.style.overflow = 'visible';
    el.style.lineHeight = '';
    el.style.left = '50%';
    el.style.transform = 'translate(-50%, -50%)';
    el.style.textAlign = 'center';

    if (availLeft <= 0) return; // header not laid out yet — leave it
    if (el.scrollWidth <= availCentered) return; // fits on one line

    // Doesn't fit — wrap to two lines.
    const MIN_CENTERED = 140;
    const centered = availCentered >= MIN_CENTERED;
    el.style.whiteSpace = 'normal';
    el.style.display = '-webkit-box';
    el.style.webkitBoxOrient = 'vertical';
    el.style.webkitLineClamp = '2';
    el.style.setProperty('line-clamp', '2');
    el.style.overflow = 'hidden';
    el.style.lineHeight = '1.15';
    el.style.overflowWrap = 'anywhere';
    if (centered) {
      el.style.maxWidth = `${Math.round(availCentered)}px`;
    } else {
      el.style.maxWidth = `${Math.round(Math.max(availLeft, 80))}px`;
      el.style.left = `${Math.round(leftR.right + GAP - opRect.left)}px`;
      el.style.transform = 'translateY(-50%)';
      el.style.textAlign = 'left';
    }
  };

  // Title is just the model name — the loaded format (sog/splat/ply/cply) is
  // shown in the Quality menu instead, not here.
  const updateModelNameEl = () => {
    const el = document.getElementById('model-name');
    if (!el || !modelNameRef.current) return;
    el.textContent = modelNameRef.current;
    requestAnimationFrame(fitModelName);
  };

  useEffect(() => {
    const onResize = () => requestAnimationFrame(fitModelName);
    window.addEventListener('resize', onResize);
    // The Inter webfont can land after first paint and shift the measured
    // width; re-fit once it's ready, plus a fallback tick for the injected
    // header settling in.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => fitModelName());
    const t = setTimeout(fitModelName, 400);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(t);
    };
  }, []);

  // The /scene viewer fills the viewport exactly and must never scroll. The
  // global `html, body, #root { overflow-x: hidden }` rule leaves overflow-y
  // computed as `auto` on all three, so anything absolutely positioned that
  // pokes past the viewport — chiefly the label tooltip SceneHandler.js
  // appends into #root (see its appendChild note) — makes that element
  // vertically scrollable, and a wheel over a non-scrollable tooltip then
  // scrolls the whole page. Lock both axes on all three for as long as the
  // viewer is mounted; other routes still scroll normally.
  useEffect(() => {
    const els = [document.documentElement, document.body, document.getElementById('root')].filter(Boolean);
    const prev = els.map((el) => el.style.overflow);
    els.forEach((el) => { el.style.overflow = 'hidden'; });
    return () => {
      els.forEach((el, i) => { el.style.overflow = prev[i]; });
    };
  }, []);

  // SceneHandler.js appends label markers, the selected-label tooltip, and
  // the point-pick placement cursor directly under #root/document.body (see
  // its own comment on why — a stacking-context issue with the WebGL
  // canvas), not inside #three-container. viewer-top.html's settings
  // dropdown (#profile-dropdown) similarly gets moved onto document.body at
  // runtime, outside #root, so it can out-rank #upload-overlay's z-index.
  // All of these are outside React's tree entirely and survive a route
  // change (e.g. navigating away, or logging out) with nothing to unmount
  // them — a selected label's marker + tooltip would otherwise keep
  // floating on top of whatever page renders next, including the login
  // screen post-logout (the dropdown itself is display:none by default so
  // it wouldn't be visibly obvious, but no reason to leave it orphaned).
  useEffect(() => {
    return () => {
      // Stop the 3D scene and release it (draw loop, listeners, timers,
      // workers, GPU) — leaving is in-app navigation, nothing reloads — and
      // scene.js's viewer session (its window listeners and scale animation).
      disposeScene();
      window.__disposeViewer?.();
      document.querySelectorAll('script[data-viewer-top]').forEach((el) => el.remove());
      if (window.__profileOutsideClick) {
        document.removeEventListener('click', window.__profileOutsideClick);
        window.__profileOutsideClick = null;
      }
      document.querySelectorAll('.label-marker, .label-tooltip, .label-placement-cursor, #profile-dropdown').forEach((el) => el.remove());
      document.body.classList.remove('label-placement-mode');

      // #upload-overlay (public/index.html) is likewise outside React's
      // tree, toggled only by SceneHandler.js's spinner() around the load.
      // Unmounting mid-load (e.g. logging out before it finishes) means
      // whatever async code was eventually going to call spinner(false)
      // either never gets there or no longer matters — nothing else ever
      // turns the overlay back off, so it was still sitting there, loading
      // ring and percentage and all, on top of whatever page renders next
      // (e.g. the login screen after logout).
      document.body.classList.remove('upload-active');
      const overlay = document.getElementById('upload-overlay');
      if (overlay) overlay.style.display = 'none';
    };
  }, []);

  // Crash recovery. SceneHandler writes `sceneLoadGuard` to sessionStorage
  // right before the heavy load work and clears it once the scene has
  // genuinely rendered. If it's still here on remount, the previous attempt
  // OOM'd the tab (Safari/Chrome kill + auto-reload keeps sessionStorage).
  // Step down one notch and reload, rather than crash-loop:
  //   1. switch off the SOG WASM-decode path (retry as .splat)
  //   2. fewer splats (100 → 75 → 50)
  //   3. lower resolution (→ 75% → 55% → 40%), splats at 50
  //   4. give up — show the "too large for this device" screen
  useEffect(() => {
    let raw
    try { raw = sessionStorage.getItem('sceneLoadGuard') } catch { return }
    if (!raw) return
    let g
    try { g = JSON.parse(raw) } catch { sessionStorage.removeItem('sceneLoadGuard'); return }
    sessionStorage.removeItem('sceneLoadGuard')

    const modelId = getViewingModelId()
    if (!modelId || g.modelId !== modelId || Date.now() - g.ts > 90_000) return

    authFetch(`${API_URL}?getModel=true&id=${modelId}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        const hasSplat = !!data?.formats?.splat?.url
        const params = new URLSearchParams(window.location.search)
        const reload = () => navigate(`${window.location.pathname}?${params.toString()}`, { replace: true })

        if (g.format !== 'splat' && hasSplat) { params.set('format', 'splat'); return reload() }

        // Load 25 points fewer splats per crash, not below 50 (g.splats is
        // what was LOADED). ?splatsLoad= caps loading; ?splats= is only the
        // share drawn, which loads at least the device's Auto amount.
        // Streamed SOG (format 'lod') ignores ?splatsLoad= — straight to
        // resolution, and none of it left in the URL.
        const streamed = g.format === 'lod'
        const nextSplats = !streamed && g.splats > 50 ? String(Math.max(50, g.splats - 25)) : null
        if (nextSplats) { params.set('splatsLoad', nextSplats); return reload() }

        // g.resolution is the resolution (%) that crashed — retry one step lower.
        const nextResolution = Number.isFinite(g.resolution) ? [75, 55, 40].find((r) => r < g.resolution) : undefined
        if (nextResolution != null) {
          if (streamed) params.delete('splatsLoad')
          else params.set('splatsLoad', '50')
          params.set('resolution', String(nextResolution))
          return reload()
        }

        setLoadCrashed(true)
      })
      .catch(() => setLoadCrashed(true))
  }, [navigate])

  // A reload, or leaving the scene, while it's still loading is not a crash
  // either: the browser fires pagehide (an out-of-memory kill doesn't), and
  // leaving the scene inside the app unmounts this. Without it the guard was
  // left behind and the next load "recovered" with ?splatsLoad=75.
  useEffect(() => {
    const clearGuard = () => { try { sessionStorage.removeItem('sceneLoadGuard') } catch { /* noop */ } }
    window.addEventListener('pagehide', clearGuard)
    return () => { window.removeEventListener('pagehide', clearGuard); clearGuard() }
  }, [])

  useEffect(() => {
    const handle = (e) => {
      // A clean failure (this handler ran) is not a tab crash — drop the guard
      // so a later revisit doesn't try to "recover" from it.
      try { sessionStorage.removeItem('sceneLoadGuard'); } catch { /* noop */ }
      if (typeof e.detail === 'string' && e.detail.includes('SOG_MOBILE_UNSUPPORTED')) {
        // Model is SOG-only — show an in-page message rather than navigating away
        const overlay = document.getElementById('upload-overlay');
        if (overlay) overlay.style.display = 'none';
        const el = document.createElement('div');
        el.style.cssText = 'position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#20252b;color:#fff;font-family:Inter,Arial,sans-serif;text-align:center;padding:24px;z-index:9999;';
        el.innerHTML = '<p style="font-size:17px;font-weight:500;margin:0 0 10px">Not available on mobile</p><p style="font-size:14px;color:rgba(255,255,255,0.55);margin:0">This model uses a format that requires a desktop browser or iPad.</p>';
        document.body.appendChild(el);
        return;
      }
      console.log('Scene load failed — redirecting');
      // A share can be revoked/expire mid-session — resolveSplatUrl's now
      // canView-gated getModel call fails, landing here. Sending a shared
      // (logged-out) visitor toward '/' just bounces them to a login wall;
      // /link-expired matches what an already-invalid link shows instead.
      navigate(sessionStorage.getItem('shareToken') ? '/link-expired' : '/');
    };
    window.addEventListener('sceneLoadFailed', handle);
    return () => window.removeEventListener('sceneLoadFailed', handle);
  }, [navigate]);

  // Load top HTML
  useEffect(() => {
    fetch('/html/viewer-top.html')
      .then((res) => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.text();
      })
      .then((html) => {
        const container = document.getElementById('html-container');
        if (container) {
          container.innerHTML = '<base href="/">' + html;

          // Re-execute inline scripts (innerHTML doesn't run them); the copies
          // are removed again when the page is left (see the unmount cleanup).
          container.querySelectorAll('script').forEach((oldScript) => {
            const newScript = document.createElement('script');
            newScript.textContent = oldScript.textContent;
            newScript.dataset.viewerTop = '1';
            document.body.appendChild(newScript);
          });

          // Inject model name
          const modelId = getViewingModelId();
          if (modelId) {
            authFetch(`${API_URL}?getModel=true&id=${modelId}`)
              .then(res => res.ok ? res.json() : null)
              .then(data => {
                if (data?.name) {
                  modelNameRef.current = data.name;
                  updateModelNameEl();
                }
              })
              .catch(() => {});
          }
        }
      })
      .catch((err) => console.error('Error loading top HTML:', err));
  }, []);

  // Load bottom HTML and scene.js
  useEffect(() => {
    let retries = 0;
    const maxRetries = 3;
    const retryDelayMs = 300; // delay before retrying on any failure
    let sceneStarted = false; // initializeScene runs once per visit (see onload)

    fetch('/html/viewer-bottom.html')
      .then((res) => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.text();
      })
      .then((html) => {
        const container = document.getElementById('html-container2');
        if (!container) return;

        container.innerHTML = '<base href="/">' + html;
        setIsBottomHtmlLoaded(true);

        // Let HTML render first
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const sceneScript = document.createElement('script');
            sceneScript.src = '/js/scene.js';
            sceneScript.dataset.viewerTop = '1'; // removed on leaving
            sceneScript.async = true;

            sceneScript.onload = () => {
              // Immediately initialize Three.js scene (your original timing)
              // — once per visit: the retries below re-run scene.js (and
              // this handler) after an initViewer error, which used to
              // start a second, parallel scene load of the same model
              // (double memory, and two loading percentages interleaving).
              if (!sceneStarted) {
                sceneStarted = true;
                try {
                  initializeScene('three-container');
                } catch (err) {
                  console.error('Error initializing 3D scene:', err);
                }
              }

              // Then delay the UI setup slightly to avoid race conditions
              const waitForDom = () => {
                const ready =
                  document.getElementById('scroll-container') &&
                  document.querySelectorAll('.menu-item').length > 0;

                if (ready) {
                  if (window.initViewer) {
                    try {
                      window.initViewer(); // runs initialize1() + initialize2()
                    } catch (err) {
                      console.error('Error running initViewer:', err);

                      // Retry after initViewer error, with delay
                      if (retries < maxRetries) {
                        retries += 1;
                        console.warn(
                          `Retrying scene.js load after initViewer error (${retries}/${maxRetries})`
                        );

                        setTimeout(() => {
                          const newScript = document.createElement('script');
                          newScript.src = '/js/scene.js';
                          newScript.dataset.viewerTop = '1'; // removed on leaving
                          newScript.async = true;
                          newScript.onload = sceneScript.onload;
                          newScript.onerror = sceneScript.onerror;

                          document.body.appendChild(newScript);
                        }, retryDelayMs);
                      }
                    }
                  }
                } else {
                  requestAnimationFrame(waitForDom);
                }
              };
              requestAnimationFrame(waitForDom);
            };

            sceneScript.onerror = (err) => {
              console.error('Error loading scene.js:', err);
              if (retries < maxRetries) {
                retries += 1;
                console.warn(`Retrying scene.js load (${retries}/${maxRetries})`);

                // Retry after network/load error, with the same delay
                setTimeout(() => {
                  const newScript = document.createElement('script');
                  newScript.src = '/js/scene.js';
                  newScript.dataset.viewerTop = '1'; // removed on leaving
                  newScript.async = true;
                  newScript.onload = sceneScript.onload;
                  newScript.onerror = sceneScript.onerror;

                  document.body.appendChild(newScript);
                }, retryDelayMs);
              }
            };

            document.body.appendChild(sceneScript);
          });
        });
      })
      .catch((err) => console.error('Error loading bottom HTML:', err));
  }, []);

  if (loadCrashed) {
    return (
      <div style={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#20252b", color: "#fff", fontFamily: "Inter,Arial,sans-serif", textAlign: "center", padding: "24px" }}>
        <p style={{ fontSize: 17, fontWeight: 500, margin: "0 0 10px" }}>Cannot load this model</p>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", margin: "0 0 20px", maxWidth: 340 }}>This device ran out of memory loading it, even at the lowest quality. Try a device with more memory.</p>
        <button onClick={() => window.history.back()} style={{ background: "none", border: "none", color: "#3d8f90", fontSize: 14, cursor: "pointer", padding: 0 }}>← Back</button>
      </div>
    )
  }

  return (
    <div className="page-container">
      {/* Top */}
      {/* position:relative is required for zIndex to have any effect at all
          (a non-positioned element always paints below positioned content
          regardless of its z-index) — without it, the opaque background
          here didn't actually occlude the label markers sliding underneath.
          The other half of that fix is in SceneHandler.js: markers must be
          appended inside #root (not document.body) — #root is
          position:fixed in index.html, which unlike relative/absolute
          always creates a new stacking context even with z-index:auto, so
          anything appended straight to body sat *outside* it entirely and
          no z-index in here could ever have beaten it regardless of value.
          Note: this zIndex can't help the settings dropdown out-rank
          #upload-overlay (z-index:999999, public/index.html) — #root itself
          has no explicit z-index, so nothing inside it can ever beat an
          explicit positive z-index outside it, no matter the value here.
          That's handled by reparenting the dropdown itself out of #root
          instead — see viewer-top.html. */}
      <div id="html-container" style={{ position: 'relative', zIndex: 10, flexShrink: 0 }} />

      {/* Three.js scene */}
      <div
        id="three-container"
        style={{ width: '100%', flexGrow: 1, display: 'block', overflow: 'hidden' }}
      />

      {/* Bottom */}
      <div id="html-container2" style={{ position: 'relative', zIndex: 10, flexShrink: 0 }} />

      <AspectRatioControls />

      {isBottomHtmlLoaded && <SceneController />}
      <LabelsPanel />
    </div>
  );
};

export default Scene;
